/**
 * EmployeeReimbursements.jsx — Employee Portal Page
 * Route: /employee/reimbursements
 *
 * Multi-step flow:
 *   Step 1 → Create claim (title + description)
 *   Step 2 → Add items (category, amount, date, receipt upload)
 *   Step 3 → Review & Submit
 * Also shows claim history with status tracking.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Trash2, Upload, FileText, CheckCircle, XCircle,
  Clock, DollarSign, ChevronLeft, ChevronRight, Eye,
  Receipt, AlertCircle, X, CloudUpload, Image, Loader,
} from "lucide-react";
import { apiBaseUrl } from "services/config";
import { useTheme } from "context/ThemeContext";

const API = apiBaseUrl;

const CATEGORIES = [
  {key:"travel",         label:"✈️ Travel & Transport"},
  {key:"accommodation",  label:"🏨 Accommodation"},
  {key:"meals",          label:"🍽️ Meals & Entertainment"},
  {key:"office_supplies",label:"📦 Office Supplies"},
  {key:"software",       label:"💻 Software & Tools"},
  {key:"training",       label:"🎓 Training & Courses"},
  {key:"medical",        label:"🏥 Medical"},
  {key:"courier",        label:"📫 Courier & Delivery"},
  {key:"telephone",      label:"📞 Telephone & Internet"},
  {key:"fuel",           label:"⛽ Fuel"},
  {key:"other",          label:"📋 Other"},
];

const STATUS_CFG = {
  draft:    {label:"Draft",    icon:Clock,       colorKey:"dim"   },
  pending:  {label:"Pending",  icon:Clock,       colorKey:"amber" },
  approved: {label:"Approved", icon:CheckCircle, colorKey:"green" },
  rejected: {label:"Rejected", icon:XCircle,     colorKey:"red"   },
  paid:     {label:"Paid",     icon:DollarSign,  colorKey:"cyan"  },
};

const fmtINR = n => `₹${Number(n||0).toLocaleString("en-IN",{maximumFractionDigits:0})}`;
const today  = () => new Date().toISOString().slice(0,10);

// ── Auth — matches employee portal pattern ─────────────────────────
// Backend employee endpoints use ?platform_user_id=N  (same as /employee-portal/*)
// get_current_user is admin-only — employee tokens return 401 with it
const userData   = JSON.parse(sessionStorage.getItem("platform_user") || "{}");
const platformId = userData.id;
const token      = sessionStorage.getItem("platform_token") || "";
const ax         = { headers: { Authorization: `Bearer ${token}` } };

// Append platform_user_id to any URL (matches /employee-portal/* pattern)
const uid = (url) =>
  `${url}${url.includes("?") ? "&" : "?"}platform_user_id=${platformId}`;

// ── Shared UI ──────────────────────────────────────────────────────
function Card({children,style={}}) {
  const {T} = useTheme();
  return <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,...style}}>{children}</div>;
}
function Btn({onClick,disabled,variant="primary",size="md",children,style={}}) {
  const {T} = useTheme();
  const bg = variant==="primary"?`linear-gradient(135deg,${T.cyan},${T.purple})`:T.toggle_bg;
  const col= variant==="primary"?"#fff":variant==="danger"?T.red:variant==="success"?T.green:T.muted;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding:size==="sm"?"5px 12px":"9px 20px",borderRadius:8,
      border:variant==="primary"?"none":`1px solid ${col}30`,
      background:variant==="primary"?bg:variant==="danger"?`${T.red}12`:variant==="success"?`${T.green}12`:T.toggle_bg,
      color:col,cursor:disabled?"not-allowed":"pointer",opacity:disabled?0.5:1,
      fontFamily:"Outfit,sans-serif",fontSize:size==="sm"?11:13,fontWeight:700,
      display:"inline-flex",alignItems:"center",gap:6,...style,
    }}>{children}</button>
  );
}
function StatusBadge({status}) {
  const {T} = useTheme();
  const cfg   = STATUS_CFG[status]||STATUS_CFG.pending;
  const color = T[cfg.colorKey]||T.muted;
  const Icon  = cfg.icon;
  return (
    <span style={{fontSize:11,padding:"3px 10px",borderRadius:20,fontWeight:700,
      background:`${color}18`,color,border:`1px solid ${color}30`,
      display:"inline-flex",alignItems:"center",gap:5}}>
      <Icon size={10}/>{cfg.label}
    </span>
  );
}

// ── Receipt uploader ───────────────────────────────────────────────
function ReceiptUploader({onUploaded}) {
  const {T} = useTheme();
  const ref         = useRef();
  const [uploading, setUploading] = useState(false);
  const [preview,   setPreview]   = useState(null);
  const [fname,     setFname]     = useState("");

  const upload = async(file) => {
    if(!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(uid(`${API}/reimbursements/upload`), fd, {headers:{...ax.headers,"Content-Type":"multipart/form-data"}});
      setFname(file.name);
      if(file.type.startsWith("image/")) setPreview(URL.createObjectURL(file));
      else setPreview(null);
      onUploaded(r.data.receipt_path, file.name);
    } catch(e) {
      alert("❌ Upload failed: "+(e.response?.data?.detail||e.message));
    } finally { setUploading(false); }
  };

  const handleDrop = e => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if(file) upload(file);
  };

  return (
    <div>
      <div
        onDragOver={e=>e.preventDefault()} onDrop={handleDrop}
        onClick={()=>ref.current?.click()}
        style={{border:`2px dashed ${fname?T.green:T.border}`,borderRadius:8,
          padding:"16px 12px",textAlign:"center",cursor:"pointer",
          background:fname?`${T.green}06`:T.inp_bg,
          transition:"all 0.2s"}}>
        {uploading ? (
          <div style={{color:T.muted,fontSize:12,display:"flex",alignItems:"center",
            justifyContent:"center",gap:6}}>
            <Loader size={14} style={{animation:"spin 1s linear infinite"}}/> Uploading…
          </div>
        ) : fname ? (
          <div style={{color:T.green,fontSize:12,display:"flex",alignItems:"center",
            justifyContent:"center",gap:6}}>
            <CheckCircle size={14}/> {fname}
          </div>
        ) : (
          <div>
            <CloudUpload size={20} style={{color:T.dim,marginBottom:6}}/>
            <div style={{fontSize:12,color:T.muted}}>Drop receipt here or click to upload</div>
            <div style={{fontSize:11,color:T.dim,marginTop:3}}>JPG, PNG, PDF — max 10MB</div>
          </div>
        )}
      </div>
      {preview && (
        <img src={preview} alt="Receipt preview"
          style={{width:"100%",maxHeight:120,objectFit:"cover",
            borderRadius:6,marginTop:6,border:`1px solid ${T.border}`}}/>
      )}
      <input ref={ref} type="file" style={{display:"none"}}
        accept=".jpg,.jpeg,.png,.pdf,.heic,.webp"
        onChange={e=>upload(e.target.files[0])}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── Add item form ──────────────────────────────────────────────────
function AddItemForm({onAdd, onCancel}) {
  const {T, theme} = useTheme();
  const [form, setForm] = useState({
    category:"travel", description:"", amount:"",
    bill_date:today(), receipt_path:"", receipt_filename:"",
  });
  const f = k => e => setForm(p=>({...p,[k]:e.target.value}));
  const inp = {
    background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:8,
    color:T.text,fontSize:13,padding:"9px 12px",outline:"none",
    fontFamily:"Outfit,sans-serif",boxSizing:"border-box",width:"100%",
  };
  const lbl = {fontSize:11,color:T.muted,display:"block",marginBottom:4,
    fontWeight:700,textTransform:"uppercase"};

  const submit = () => {
    if(!form.description||!form.amount){alert("Fill description and amount");return;}
    onAdd({
      ...form,
      amount: parseFloat(form.amount),
    });
    setForm({category:"travel",description:"",amount:"",bill_date:today(),
      receipt_path:"",receipt_filename:""});
  };

  return (
    <div style={{padding:"16px",borderRadius:10,
      background:T.card2,border:`1px solid ${T.border}`,
      display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:13,fontWeight:700,color:T.text}}>+ Add Item</div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <div>
          <label style={lbl}>Category *</label>
          <select value={form.category} onChange={f("category")} style={{...inp,cursor:"pointer"}}>
            {CATEGORIES.map(c=>(
              <option key={c.key} value={c.key} style={{background:T.opt_bg}}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={lbl}>Amount (₹) *</label>
          <input type="number" min="0" step="0.01" value={form.amount}
            onChange={f("amount")} placeholder="0.00" style={inp}/>
        </div>
      </div>

      <div>
        <label style={lbl}>Description *</label>
        <input value={form.description} onChange={f("description")}
          placeholder="e.g. Cab from office to airport" style={inp}/>
      </div>

      <div>
        <label style={lbl}>Bill Date *</label>
        <input type="date" value={form.bill_date} onChange={f("bill_date")}
          style={{...inp,colorScheme:theme}}/>
      </div>

      <div>
        <label style={lbl}>Receipt (optional)</label>
        <ReceiptUploader
          onUploaded={(path,name)=>setForm(p=>({...p,receipt_path:path,receipt_filename:name}))}/>
      </div>

      <div style={{display:"flex",gap:8}}>
        <Btn onClick={submit} variant="primary" size="sm">
          <Plus size={12}/>Add Item
        </Btn>
        <Btn onClick={onCancel} variant="ghost" size="sm">Cancel</Btn>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// CLAIM DETAIL (read-only view for employee)
// ══════════════════════════════════════════════════════════════════
function ClaimDetail({claim, onBack}) {
  const {T} = useTheme();
  return (
    <div>
      <button onClick={onBack} style={{background:"none",border:`1px solid ${T.border}`,
        borderRadius:8,padding:"7px 14px",cursor:"pointer",color:T.muted,
        fontFamily:"Outfit,sans-serif",fontSize:13,display:"flex",alignItems:"center",gap:6,marginBottom:20}}>
        <ChevronLeft size={14}/> Back to Claims
      </button>

      <Card style={{padding:"20px 24px",marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
          <div>
            <h3 style={{margin:0,fontSize:18,fontWeight:700,color:T.text}}>{claim.title}</h3>
            {claim.description&&<p style={{fontSize:13,color:T.muted,margin:"6px 0 0"}}>{claim.description}</p>}
          </div>
          <StatusBadge status={claim.status}/>
        </div>
        <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
          {[
            ["Submitted", claim.submitted_at],
            ["Total Claimed", fmtINR(claim.total_amount)],
            claim.approved_amount&&["Approved Amount", fmtINR(claim.approved_amount)],
            claim.paid_at&&["Paid On", claim.paid_at],
          ].filter(Boolean).map(([label,val])=>(
            <div key={label}>
              <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase"}}>{label}</div>
              <div style={{fontSize:14,fontWeight:700,color:T.text,marginTop:2}}>{val}</div>
            </div>
          ))}
        </div>
        {claim.reviewer_notes && (
          <div style={{marginTop:14,padding:"12px 14px",borderRadius:8,
            background:claim.status==="rejected"?`${T.red}08`:`${T.green}08`,
            border:`1px solid ${claim.status==="rejected"?T.red:T.green}25`}}>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:4}}>
              {claim.status==="rejected"?"REJECTION REASON":"REVIEWER NOTE"}
            </div>
            <div style={{fontSize:13,color:T.text}}>{claim.reviewer_notes}</div>
          </div>
        )}
      </Card>

      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>
        Items ({claim.items?.length||0})
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {(claim.items||[]).map(item=>(
          <Card key={item.id} style={{padding:"14px 16px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{fontSize:11,padding:"2px 8px",borderRadius:20,fontWeight:700,
                    background:`${T.purple}18`,color:T.purple}}>
                    {CATEGORIES.find(c=>c.key===item.category)?.label||item.category}
                  </span>
                  {item.has_receipt&&(
                    <span style={{fontSize:10,padding:"2px 7px",borderRadius:20,fontWeight:700,
                      background:`${T.green}12`,color:T.green}}>✓ Receipt</span>
                  )}
                </div>
                <div style={{fontSize:14,fontWeight:600,color:T.text}}>{item.description}</div>
                <div style={{fontSize:12,color:T.dim,marginTop:3}}>{item.bill_date}</div>
              </div>
              <div style={{fontSize:18,fontWeight:900,color:T.cyan,
                fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(item.amount)}</div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// NEW CLAIM WIZARD
// ══════════════════════════════════════════════════════════════════
function NewClaimWizard({onDone, onCancel}) {
  const {T} = useTheme();
  const [step,    setStep]    = useState(1);   // 1=details, 2=items, 3=review
  const [claim,   setClaim]   = useState(null);
  const [items,   setItems]   = useState([]);
  const [title,   setTitle]   = useState("");
  const [desc,    setDesc]    = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const inp = {
    background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:8,
    color:T.text,fontSize:13,padding:"9px 12px",outline:"none",
    fontFamily:"Outfit,sans-serif",boxSizing:"border-box",width:"100%",
  };

  // Step 1 → create claim
  const createClaim = async() => {
    if(!title.trim()){alert("Enter a title");return;}
    setSaving(true);
    try {
      const r = await axios.post(uid(`${API}/reimbursements/`), {title, description:desc}, ax);
      setClaim(r.data);
      setStep(2);
    } catch(e){alert("❌ "+(e.response?.data?.detail||"Error"));}
    finally{setSaving(false);}
  };

  // Step 2 → add item to claim
  const addItem = async(itemData) => {
    try {
      await axios.post(uid(`${API}/reimbursements/${claim.id}/items`), itemData, ax);
      setItems(p=>[...p,itemData]);
      setShowAdd(false);
    } catch(e){alert("❌ "+(e.response?.data?.detail||"Error"));}
  };

  const removeItem = async(idx) => {
    // Items are tracked locally before submit — just remove from list
    // (items are already saved in backend as we go)
    setItems(p=>p.filter((_,i)=>i!==idx));
  };

  // Step 3 → submit
  const submit = async() => {
    if(items.length===0){alert("Add at least one item");return;}
    setSaving(true);
    try {
      await axios.put(uid(`${API}/reimbursements/${claim.id}/submit`), {}, ax);
      onDone();
    } catch(e){alert("❌ "+(e.response?.data?.detail||"Error"));}
    finally{setSaving(false);}
  };

  const totalAmount = items.reduce((a,i)=>a+parseFloat(i.amount||0),0);

  return (
    <div>
      {/* Step indicator */}
      <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:24}}>
        {[{n:1,label:"Details"},{n:2,label:"Add Items"},{n:3,label:"Submit"}].map((s,i)=>(
          <React.Fragment key={s.n}>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
              <div style={{width:32,height:32,borderRadius:"50%",
                background:step>=s.n?`linear-gradient(135deg,${T.cyan},${T.purple})`:T.toggle_bg,
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:13,fontWeight:700,
                color:step>=s.n?"#fff":T.dim}}>
                {step>s.n?<CheckCircle size={14}/>:s.n}
              </div>
              <div style={{fontSize:10,color:step>=s.n?T.cyan:T.dim,fontWeight:700}}>{s.label}</div>
            </div>
            {i<2&&<div style={{flex:1,height:2,background:step>s.n?T.cyan:T.border,
              margin:"0 6px",marginBottom:14,transition:"background 0.3s"}}/>}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 — Claim details */}
      {step===1 && (
        <Card style={{padding:"24px"}}>
          <h3 style={{margin:"0 0 20px",fontSize:16,fontWeight:700,color:T.text}}>
            Claim Details
          </h3>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div>
              <label style={{fontSize:11,color:T.muted,display:"block",marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>
                Claim Title *
              </label>
              <input value={title} onChange={e=>setTitle(e.target.value)}
                placeholder="e.g. Client visit — Bangalore trip" style={inp}/>
            </div>
            <div>
              <label style={{fontSize:11,color:T.muted,display:"block",marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>
                Description
              </label>
              <textarea value={desc} onChange={e=>setDesc(e.target.value)}
                placeholder="Brief description of the expense…" rows={3}
                style={{...inp,resize:"vertical"}}/>
            </div>
            <div style={{display:"flex",gap:10,paddingTop:4}}>
              <Btn onClick={createClaim} disabled={saving}>
                {saving?"Creating…":<>Next: Add Items <ChevronRight size={14}/></>}
              </Btn>
              <Btn onClick={onCancel} variant="ghost">Cancel</Btn>
            </div>
          </div>
        </Card>
      )}

      {/* Step 2 — Items */}
      {step===2 && (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Card style={{padding:"20px 24px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div>
                <h3 style={{margin:0,fontSize:15,fontWeight:700,color:T.text}}>{title}</h3>
                <div style={{fontSize:12,color:T.muted,marginTop:3}}>
                  {items.length} item{items.length!==1?"s":""} · Total: {" "}
                  <strong style={{color:T.cyan}}>{fmtINR(totalAmount)}</strong>
                </div>
              </div>
              <Btn onClick={()=>setShowAdd(true)} size="sm">
                <Plus size={12}/>Add Item
              </Btn>
            </div>

            {/* Items list */}
            {items.length===0 ? (
              <div style={{padding:"32px 0",textAlign:"center",color:T.dim,fontSize:13}}>
                No items yet — click Add Item to start
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
                {items.map((item,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:12,
                    padding:"10px 12px",borderRadius:8,background:T.card2,
                    border:`1px solid ${T.border}`}}>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                        <span style={{fontSize:10,padding:"2px 7px",borderRadius:20,fontWeight:700,
                          background:`${T.purple}18`,color:T.purple}}>
                          {CATEGORIES.find(c=>c.key===item.category)?.label||item.category}
                        </span>
                        {item.receipt_filename&&(
                          <span style={{fontSize:10,color:T.green}}>📎 {item.receipt_filename}</span>
                        )}
                      </div>
                      <div style={{fontSize:13,color:T.text}}>{item.description}</div>
                      <div style={{fontSize:11,color:T.dim}}>{item.bill_date}</div>
                    </div>
                    <div style={{fontSize:15,fontWeight:800,color:T.cyan,
                      fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(item.amount)}</div>
                    <button onClick={()=>removeItem(i)}
                      style={{background:"none",border:"none",cursor:"pointer",
                        color:T.red,opacity:0.7,padding:4}}>
                      <Trash2 size={13}/>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {showAdd && (
              <AddItemForm onAdd={addItem} onCancel={()=>setShowAdd(false)}/>
            )}
          </Card>

          {items.length>0 && (
            <div style={{display:"flex",gap:10}}>
              <Btn onClick={()=>setStep(3)}>
                Review & Submit <ChevronRight size={14}/>
              </Btn>
              <Btn onClick={()=>setShowAdd(true)} variant="ghost" size="sm">
                <Plus size={12}/>Add More Items
              </Btn>
            </div>
          )}
        </div>
      )}

      {/* Step 3 — Review & submit */}
      {step===3 && (
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Card style={{padding:"20px 24px"}}>
            <h3 style={{margin:"0 0 16px",fontSize:15,fontWeight:700,color:T.text}}>
              Review & Submit
            </h3>

            {/* Summary */}
            <div style={{padding:"14px 16px",borderRadius:10,marginBottom:16,
              background:`${T.cyan}08`,border:`1px solid ${T.cyan}20`}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <span style={{fontSize:13,color:T.muted}}>Claim Title</span>
                <span style={{fontSize:13,fontWeight:600,color:T.text}}>{title}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <span style={{fontSize:13,color:T.muted}}>Total Items</span>
                <span style={{fontSize:13,fontWeight:600,color:T.text}}>{items.length}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <span style={{fontSize:13,color:T.muted}}>Total Amount</span>
                <span style={{fontSize:18,fontWeight:900,color:T.cyan,
                  fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(totalAmount)}</span>
              </div>
            </div>

            {/* Item summary */}
            {items.map((item,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",
                padding:"8px 0",borderBottom:`1px solid ${T.border}30`,fontSize:13}}>
                <span style={{color:T.muted}}>{item.description}</span>
                <span style={{color:T.text,fontWeight:600}}>{fmtINR(item.amount)}</span>
              </div>
            ))}

            <div style={{marginTop:16,padding:"11px 14px",borderRadius:8,
              background:`${T.amber}08`,border:`1px solid ${T.amber}20`,
              fontSize:12,color:T.muted}}>
              ℹ️ Once submitted, you cannot edit this claim. HR will review and notify you of the decision.
            </div>
          </Card>

          <div style={{display:"flex",gap:10}}>
            <Btn onClick={submit} disabled={saving} variant="success">
              {saving?"Submitting…":<><CheckCircle size={13}/>Submit Claim</>}
            </Btn>
            <Btn onClick={()=>setStep(2)} variant="ghost">
              <ChevronLeft size={14}/>Back to Items
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════
export default function EmployeeReimbursements() {
  const {T} = useTheme();
  const [view,   setView]   = useState("list");  // list | new | detail
  const [claims, setClaims] = useState([]);
  const [sel,    setSel]    = useState(null);
  const [loading,setLoading]= useState(true);
  const [toast,  setToast]  = useState("");

  const flash = msg => { setToast(msg); setTimeout(()=>setToast(""),4000); };

  const load = useCallback(async()=>{
    setLoading(true);
    try {
      const r = await axios.get(uid(`${API}/reimbursements/my`), ax);
      setClaims(r.data||[]);
    } catch{ flash("❌ Failed to load claims"); }
    finally{ setLoading(false); }
  },[]);

  useEffect(()=>{load();},[load]);

  const pending   = claims.filter(c=>c.status==="pending").length;
  const approved  = claims.filter(c=>c.status==="approved").length;
  const totalPaid = claims.filter(c=>c.status==="paid")
    .reduce((a,c)=>a+(c.approved_amount||c.total_amount),0);

  return (
    <div style={{fontFamily:"Outfit,sans-serif",color:T.text}}>
      {/* Header */}
      <div style={{marginBottom:22,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <div style={{fontSize:11,color:T.cyan,fontWeight:700,letterSpacing:"0.1em",
            textTransform:"uppercase",marginBottom:4}}>EXPENSE</div>
          <h2 style={{fontSize:22,fontWeight:800,margin:"0 0 4px",color:T.text}}>
            My Reimbursements
          </h2>
          <p style={{fontSize:13,color:T.muted,margin:0}}>
            Submit expense claims with receipts — HR reviews and approves
          </p>
        </div>
        {view==="list" && (
          <Btn onClick={()=>setView("new")}>
            <Plus size={14}/>New Claim
          </Btn>
        )}
      </div>

      {/* Views */}
      {view==="new" ? (
        <NewClaimWizard
          onDone={()=>{load();setView("list");flash("✅ Claim submitted for review");}}
          onCancel={()=>setView("list")}/>

      ) : view==="detail" && sel ? (
        <ClaimDetail claim={sel} onBack={()=>{setSel(null);setView("list");}}/>

      ) : (
        <>
          {/* Summary stats */}
          <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:120,padding:"14px 16px",borderRadius:10,
              background:`${T.amber}08`,border:`1px solid ${T.amber}20`}}>
              <div style={{fontSize:10,color:T.muted,fontWeight:700,
                textTransform:"uppercase",marginBottom:4}}>Pending</div>
              <div style={{fontSize:24,fontWeight:900,color:T.amber}}>{pending}</div>
            </div>
            <div style={{flex:1,minWidth:120,padding:"14px 16px",borderRadius:10,
              background:`${T.green}08`,border:`1px solid ${T.green}20`}}>
              <div style={{fontSize:10,color:T.muted,fontWeight:700,
                textTransform:"uppercase",marginBottom:4}}>Approved</div>
              <div style={{fontSize:24,fontWeight:900,color:T.green}}>{approved}</div>
            </div>
            <div style={{flex:1,minWidth:120,padding:"14px 16px",borderRadius:10,
              background:`${T.cyan}08`,border:`1px solid ${T.cyan}20`}}>
              <div style={{fontSize:10,color:T.muted,fontWeight:700,
                textTransform:"uppercase",marginBottom:4}}>Total Received</div>
              <div style={{fontSize:24,fontWeight:900,color:T.cyan,
                fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(totalPaid)}</div>
            </div>
          </div>

          {/* Claims list */}
          {loading ? (
            <div style={{padding:40,textAlign:"center",color:T.muted}}>Loading…</div>
          ) : claims.length===0 ? (
            <Card style={{padding:"56px 24px",textAlign:"center"}}>
              <Receipt size={40} style={{color:T.dim,marginBottom:14}}/>
              <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:6}}>
                No claims yet
              </div>
              <div style={{fontSize:13,color:T.muted,marginBottom:20}}>
                Submit your first expense claim for reimbursement
              </div>
              <Btn onClick={()=>setView("new")}><Plus size={13}/>Create First Claim</Btn>
            </Card>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {claims.map(claim=>(
                <Card key={claim.id} style={{padding:"16px 20px",cursor:"pointer",
                  transition:"border-color 0.2s"}}
                  onClick={()=>{
                    // Data already loaded with items via GET /reimbursements/my
                    // No separate API call needed — avoids hitting HR-only endpoint
                    setSel(claim); setView("detail");
                  }}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:3}}>
                        {claim.title}
                      </div>
                      <div style={{fontSize:12,color:T.dim}}>
                        {claim.item_count} item{claim.item_count!==1?"s":""} · Submitted {claim.submitted_at}
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <StatusBadge status={claim.status}/>
                      <div style={{fontSize:16,fontWeight:900,color:T.cyan,
                        fontFamily:"JetBrains Mono,monospace",marginTop:6}}>
                        {fmtINR(claim.total_amount)}
                      </div>
                    </div>
                  </div>
                  {/* Status progress bar */}
                  <div style={{display:"flex",gap:3,marginTop:8}}>
                    {["pending","approved","paid"].map((s,i)=>{
                      const steps=["pending","approved","paid"];
                      const curr =steps.indexOf(claim.status);
                      const done =i<=curr && claim.status!=="rejected";
                      const color=done?T.cyan:T.toggle_bg;
                      return (
                        <div key={s} style={{flex:1,height:3,borderRadius:3,
                          background:claim.status==="rejected"?T.red:color,transition:"background 0.3s"}}/>
                      );
                    })}
                  </div>
                  {claim.reviewer_notes&&(
                    <div style={{fontSize:12,color:claim.status==="rejected"?T.red:T.muted,
                      marginTop:6,fontStyle:"italic"}}>
                      "{claim.reviewer_notes}"
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Toast */}
      {toast&&(
        <div style={{position:"fixed",bottom:28,left:"50%",transform:"translateX(-50%)",
          padding:"10px 22px",borderRadius:10,fontSize:13,fontWeight:600,zIndex:9999,
          background:T.card,
          border:`1px solid ${toast.startsWith("✅")?`${T.green}50`:`${T.red}50`}`,
          color:toast.startsWith("✅")?T.green:T.red,
          boxShadow:"0 8px 32px rgba(0,0,0,0.4)",fontFamily:"Outfit,sans-serif"}}>
          {toast}
        </div>
      )}
    </div>
  );
}